import React, {Key, useState, useMemo} from 'react'
import {useShiftSelect} from '../hooks/useShiftSelect'
import {classNames} from '../wrapper'
import CheckBox from './CheckBox'
import {ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon, ArrowPathIcon} from '@heroicons/react/24/outline'

export enum CountDisplay {
    /** Shows just the number selected (e.g. "3"). */
    Count = 'count',
    /** Also shows how many rows are currently loaded (e.g. "3 / 20"). */
    CountOfTotal = 'countOfTotal',
}

export interface SelectionProps<TKey extends Key> {
    selectedKeys: TKey[];
    onSelectionChange: (keys: TKey[]) => void;
    selectAll?: boolean;
    countDisplay?: CountDisplay;
}

export interface TableColumn<TResponse> {
    header: React.ReactNode | (() => React.ReactNode);
    row: (item: TResponse, index: number) => React.ReactNode;
    key?: Key;
    colspan?: (item: TResponse) => number;
    classNames?: (item: TResponse) => string;
    /** If provided, enables sorting for this column. Return the value to sort by. */
    sortValue?: (item: TResponse) => string | number | null | undefined;
    /**
     * If provided, enables cycle sorting for this column.
     * Click cycles through each value (putting matching rows first), then clears.
     * Requires sortValue to also be set.
     */
    cycleSortValues?: (string | number)[];
}

export interface TableCoreProps<TData, TKey extends Key = Key> {
    data: TData[];
    columns: TableColumn<TData>[];
    keyExtractor: (item: TData, index: number) => TKey;

    // Selection
    selection?: SelectionProps<TKey>;

    // Row interactions
    rowClick?: (item: TData, index: number) => void;
    rowDoubleClick?: (item: TData, index: number) => void;

    // Styling
    headerClass?: string;
    rowClass?: (item: TData, index: number) => string;
    cellClass?: string;

    // Grouping
    groupBy?: (item: TData | undefined) => number;
}

export type SortDirection = 'asc' | 'desc';

const TableCore = <TData, TKey extends Key = Key>(props: TableCoreProps<TData, TKey>) => {
    const {data, columns, keyExtractor, selection, rowClick, rowDoubleClick, groupBy} = props;

    // Sorting state
    const [sortColumn, setSortColumn] = useState<Key | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    // For cycle sorting: which index in cycleSortValues is currently prioritized (null = no cycle sort)
    const [cycleIndex, setCycleIndex] = useState<number | null>(null);

    const handleSort = (columnKey: Key, column: TableColumn<TData>) => {
        if (column.cycleSortValues) {
            // Cycle sorting mode
            if (sortColumn === columnKey && cycleIndex !== null) {
                const nextIndex = cycleIndex + 1;
                if (nextIndex >= column.cycleSortValues.length) {
                    // Clear sorting
                    setSortColumn(null);
                    setCycleIndex(null);
                    // Clear selections when rows cycle to prevent accidental actions on rows that moved out of view
                    selection?.onSelectionChange([]);
                } else {
                    setCycleIndex(nextIndex);
                    // Clear selections when rows cycle to prevent accidental actions on rows that moved out of view
                    selection?.onSelectionChange([]);
                }
            } else {
                // Start cycle sorting from first value
                setSortColumn(columnKey);
                setCycleIndex(0);
                // Clear selections when rows cycle to prevent accidental actions on rows that moved out of view
                selection?.onSelectionChange([]);
            }
        } else {
            // Standard asc/desc sorting
            setCycleIndex(null);
            if (sortColumn === columnKey) {
                // Toggle direction or clear sort
                if (sortDirection === 'asc') {
                    setSortDirection('desc');
                } else {
                    setSortColumn(null);
                    setSortDirection('asc');
                }
            } else {
                setSortColumn(columnKey);
                setSortDirection('asc');
            }
        }
    };

    // Sort data if a column is selected, keeping track of original indices
    const sortedDataWithIndices = useMemo(() => {
        const indexed = data.map((item, originalIndex) => ({item, originalIndex}));

        if (sortColumn === null) return indexed;

        const sortableColumn = columns.find((col, i) => (col.key ?? i) === sortColumn);
        if (!sortableColumn?.sortValue) return indexed;

        // Cycle sorting mode: put rows matching the prioritized value first
        if (sortableColumn.cycleSortValues && cycleIndex !== null) {
            const priorityValue = sortableColumn.cycleSortValues[cycleIndex];
            return [...indexed].sort((a, b) => {
                const aVal = sortableColumn.sortValue!(a.item);
                const bVal = sortableColumn.sortValue!(b.item);

                const aMatches = aVal === priorityValue;
                const bMatches = bVal === priorityValue;

                // Matching rows come first
                if (aMatches && !bMatches) return -1;
                if (!aMatches && bMatches) return 1;

                // Within each group, maintain original order
                return a.originalIndex - b.originalIndex;
            });
        }

        // Standard sorting
        const sorted = [...indexed].sort((a, b) => {
            const aVal = sortableColumn.sortValue!(a.item);
            const bVal = sortableColumn.sortValue!(b.item);

            // Handle null/undefined values - sort them to the end
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            // Compare values
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return aVal.localeCompare(bVal, undefined, {numeric: true, sensitivity: 'base'});
            }

            if (aVal < bVal) return -1;
            if (aVal > bVal) return 1;
            return 0;
        });

        return sortDirection === 'desc' ? sorted.reverse() : sorted;
    }, [data, sortColumn, sortDirection, cycleIndex, columns]);

    // Selection helpers
    const getRowKey = (row: TData, index: number): TKey => keyExtractor(row, index);

    const handleCheckboxClick = useShiftSelect({
        selectedKeys: selection?.selectedKeys ?? [],
        onSelectionChange: selection?.onSelectionChange ?? (() => {}),
        getKeyAtIndex: (displayIndex) => {
            const entry = sortedDataWithIndices[displayIndex];
            if (!entry) return undefined;
            return getRowKey(entry.item, entry.originalIndex);
        },
        getKeysInRange: (start, end) =>
            sortedDataWithIndices
                .slice(start, end + 1)
                .map(({item, originalIndex}) => getRowKey(item, originalIndex)),
        // Reset anchor when sort changes to prevent stale index references
        resetKey: `${sortColumn}-${sortDirection}-${cycleIndex}`,
    });

    const allSelected = (): boolean => {
        if (!selection || data.length === 0) return false;
        return data.every((item, index) => selection.selectedKeys.includes(getRowKey(item, index)));
    };

    const toggleSelectAll = () => {
        if (!selection) return;
        if (allSelected()) {
            // Deselect all visible items
            const visibleKeys = data.map((item, index) => getRowKey(item, index));
            selection.onSelectionChange(selection.selectedKeys.filter(k => !visibleKeys.includes(k)));
        } else {
            // Select all visible items (keep existing selections)
            const visibleKeys = data.map((item, index) => getRowKey(item, index));
            const newKeys = [...selection.selectedKeys];
            visibleKeys.forEach(key => {
                if (!newKeys.includes(key)) {
                    newKeys.push(key);
                }
            });
            selection.onSelectionChange(newKeys);
        }
    };

    const getRowClass = (item: TData, displayIndex: number, originalIndex: number): string => {
        const baseClass = classNames(
            displayIndex % 2 === 0 ? '' : 'bg-gray-50',
            'group hover:bg-gray-200 cursor-pointer',
            selection ? 'select-none' : ''
        );

        // For grouping, use the sorted data to check previous item
        const prevItem = displayIndex > 0 ? sortedDataWithIndices[displayIndex - 1]?.item : undefined;
        const groupClass = groupBy && groupBy(item) !== groupBy(prevItem)
            ? 'border-t-4 border-primary'
            : '';

        const customClass = props.rowClass?.(item, originalIndex) ?? '';

        return classNames(baseClass, groupClass, customClass);
    };

    const headerClass = props.headerClass ?? 'bg-gray-50';
    const cellClass = props.cellClass ?? 'whitespace-nowrap px-1 py-2 text-sm text-gray-500';

    return (
        <div className="w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300 w-full">
                <thead className={headerClass}>
                    <tr>
                        {selection && (
                            <th scope="col" className="px-2 py-2 text-center w-12">
                                {selection.selectAll !== false && (
                                    <CheckBox
                                        checked={allSelected()}
                                        onChange={() => toggleSelectAll()}
                                    />
                                )}
                                {selection.countDisplay && (
                                    <div className="text-[10px] text-primary-500 font-normal normal-case whitespace-nowrap">
                                        {selection.countDisplay === CountDisplay.CountOfTotal
                                            ? `${selection.selectedKeys.length} / ${data.length}`
                                            : `${selection.selectedKeys.length}`}
                                    </div>
                                )}
                            </th>
                        )}
                        {columns.map((col, i) => {
                            const columnKey = col.key ?? i;
                            const isSortable = !!col.sortValue;
                            const isActiveSort = sortColumn === columnKey;
                            const isCycleSort = !!col.cycleSortValues;

                            return (
                                <th
                                    scope="col"
                                    className="uppercase px-2 py-2 text-gray-500 text-left text-sm font-semibold"
                                    key={columnKey}
                                >
                                    <div className="flex items-center gap-1">
                                        <div>{typeof col.header === 'function' ? col.header() : col.header}</div>
                                        {isSortable && (
                                            <span
                                                className="inline-flex items-center cursor-pointer select-none p-1 rounded hover:bg-gray-200"
                                                onClick={() => handleSort(columnKey, col)}
                                            >
                                                {isCycleSort ? (
                                                    <span className="relative">
                                                        <ArrowPathIcon className={classNames(
                                                            "h-4 w-4",
                                                            isActiveSort && cycleIndex !== null
                                                                ? "text-primary"
                                                                : "text-gray-400"
                                                        )} />
                                                        {isActiveSort && cycleIndex !== null && (
                                                            <span className="absolute -top-1 -right-1 h-3 w-3 text-[8px] font-bold bg-primary text-white rounded-full flex items-center justify-center">
                                                                {cycleIndex + 1}
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    isActiveSort ? (
                                                        sortDirection === 'asc'
                                                            ? <ChevronUpIcon className="h-4 w-4 text-primary" />
                                                            : <ChevronDownIcon className="h-4 w-4 text-primary" />
                                                    ) : (
                                                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                                                    )
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {sortedDataWithIndices.map(({item: row, originalIndex}, displayIndex) => (
                        <tr
                            key={keyExtractor(row, originalIndex)}
                            className={getRowClass(row, displayIndex, originalIndex)}
                            onClick={() => rowClick?.(row, originalIndex)}
                            onDoubleClick={() => rowDoubleClick?.(row, originalIndex)}
                        >
                            {selection && (
                                <td
                                    className="whitespace-nowrap px-2 py-2 text-center w-12 select-none"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCheckboxClick(e, displayIndex);
                                    }}
                                >
                                    <div className="pointer-events-none">
                                        <CheckBox checked={selection.selectedKeys.includes(getRowKey(row, originalIndex))} />
                                    </div>
                                </td>
                            )}
                            {columns.map((col, colIndex) => {
                                const colspan = col.colspan?.(row) ?? 1;
                                if (colspan === 0) return null;

                                return (
                                    <td
                                        key={col.key ?? colIndex}
                                        colSpan={colspan}
                                        className={classNames(cellClass, col.classNames?.(row))}
                                    >
                                        {col.row(row, originalIndex)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TableCore;
